// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { NavigationEnd, Event, Router } from '@angular/router';

// Rxjs imports
import { Subscription } from 'rxjs';

// 3rd party imports
import notify from 'devextreme/ui/notify';
import { alert, custom } from 'devextreme/ui/dialog';
import { Properties as dxButtonOptions } from 'devextreme/ui/button';
import { DxDropDownButtonComponent } from 'devextreme-angular';

// CC-Common imports
import { CCLocalStorageCategory, CCLocalStorageService, IconConstants, MessageBoxComponent, Utils } from 'cc-common';

// Constant imports
import { StorageKeyConstants } from '../../_constants/storage-key-constants';
import { MessageInterval } from '../../_constants/messages-intervals';

// Component imports
import { BaseComponent } from '../../_base/mp-base-component';
import { PreferencesComponent } from '../../user-module/preferences/preferences.component';
import { CommunityOwnerAlertsComponent } from '../../shared-module/community-owner-alerts/community-owner-alerts.component';

// Service imports
import { AuthService } from '../../_services/auth.service';
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { HelperService } from '../../_services/shared-services/helper.service';
import { NavigationService } from '../../_services/shared-services/navigation.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { PortalAnnouncementService } from '../../_services/data-services/portal-announcement.service';
import { PropertyConveyanceService } from '../../property-conveyance-module/_services/property-conveyance.service';
import { DashboardService } from '../../community-module/_services/dashboard.service';

// Model imports
import { ActionAccessListParameterDto } from '../../_models/action-access-list-parameter-dto';
import { ActionAccessResultDto } from '../../_models/action-access-result-dto';
import { KeyValueResultDto } from '../../_models/key-value-result-dto';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { ConveyancePropertyRawDto } from '../../property-conveyance-module/_models/conveyance-property-raw-dto';
import { UnconveyancePropertyEditorResultDto } from '../../property-conveyance-module/_models/unconveyance-property-editor-result-dto';
import { UnconveyPropertyResultDto } from '../../property-conveyance-module/_models/unconvey-property-result-dto';
import { UnconveyPropertyParametersDto } from '../../property-conveyance-module/_models/unconvey-property-parameters-dto';
import { BoardApprovalOpenItemCountResultDto } from '../../community-module/_models/dashboard/board-approval-open-item-count-result-dto';

interface IActionItem {
    code: string;
    title: string;
    internalOnly: boolean;
    specificRoleOnly: boolean;
    path?: string;
}

@Component({
    selector: 'app-header',
    templateUrl: 'header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() menuToggleEnabled = false;
    @Input() menuUserEnabled = false;
    @Output() menuToggle = new EventEmitter<boolean>();

    @ViewChild('preferenceComponent', { static: false }) preferenceComponent: PreferencesComponent;
    @ViewChild('contextAlerts', { static: false }) contextAlerts: CommunityOwnerAlertsComponent;
    @ViewChild('actionsDropDownButton', { static: false }) actionsDropDownButton: DxDropDownButtonComponent;
    @ViewChild('unconveyanceMessageBox', { static: false }) unconveyanceMessageBox: MessageBoxComponent;

    logoLocation = 'before';
    userMenuItems = [];

    menuButtonWidth: string;
    isSmallScreen: boolean;
    hasContext: boolean;
    isInternalUser: boolean;
    showOwnerName = true;
    announcementCount = 0;
    isCiraBooksView = false;
    hasClassicNavigator = false;
    autoShowAnnouncements = true;
    showManageQaUsers = false;
    hasMultipleCommunities = false;
    otherCommunities: Array<KeyValueResultDto>;
    showCommunities = false;

    selectedContext: PropertyCommunityInfoResultDto;
    contextSubscription: Subscription;

    actionItems: IActionItem[] = [];

    displayWelcomeLetter = false;
    displayLogReturnedMail = false;
    displayPortalAnnouncements = false;
    displayResidentAnnouncements = false;

    menuOpened = true;
    unconveyanceOwnerInfo: UnconveyancePropertyEditorResultDto;

    loadingVisible = false;

    iconTransition = IconConstants.IN_TRANSITION;
    iconTerminate = IconConstants.TERMINATE;
    iconExclamatory = IconConstants.EXCLAMATION_CIRCLE;
    iconCancel = IconConstants.CANCEL;
    iconSubmit = IconConstants.SUBMIT;
    iconHome = IconConstants.HOME;
    iconMultiCommunities = `fas fa-clinic-medical fa-2x`;

    showConveyProperty = false;
    showExtendSession = false;

    passwordMode: string;
    userPassword: string;
    conveyPropertyUrl: string;
    sessionEndsIn: Array<string>;
    passwordShowButton: dxButtonOptions;
    passwordHideButton: dxButtonOptions;

    hasBoardApprovalAccess = false;
    baordApprovalOpenItemsCount = 0;
    isNotCurrentOwner: boolean;
    showPopover = false;
    currentTarget: string;
    currentPosition: string;
    isCommunityInProduction = false;

    constructor(
        protected readonly screenService: ScreenService,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly userService: UserService,
        private readonly helperService: HelperService,
        private readonly navigationService: NavigationService,
        private readonly contextManagerService: ContextManagerService,
        private readonly errorService: ServerErrorService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly portalAnnoucementService: PortalAnnouncementService,
        private readonly propertyConveyanceService: PropertyConveyanceService,
        private readonly dashboardService: DashboardService
    ) {
        super(screenService);
        this.passwordMode = 'password';
        this.passwordShowButton = {
            type: 'default',
            stylingMode: 'text',
            hint: 'Show Password',
            icon: IconConstants.VIEW,
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
        this.passwordHideButton = {
            type: 'default',
            stylingMode: 'text',
            hint: 'Hide Password',
            icon: IconConstants.HIDE,
            onClick: () => {
                this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
            }
        };
    }

    ngOnInit(): void {
        this.isInternalUser = this.userService.isInternal();

        this.actionItems = [
            { code: 'PROPERTY_CONTACTS', title: 'Property Contacts', internalOnly: false, specificRoleOnly: false },
            {
                code: 'PROPERTY_OWNER_WELCOME_LETTER',
                title: 'Preview / Send Welcome Letter',
                internalOnly: false,
                specificRoleOnly: false
            },
            {
                code: 'PROPERTY_LOG_RETURNED_MAIL',
                title: 'Log Returned Mail',
                internalOnly: true,
                specificRoleOnly: false
            },
            {
                code: 'PROPERTY_CONVEYANCE',
                title: 'Convey Property',
                internalOnly: true,
                specificRoleOnly: true
            },
            {
                code: 'UNCONVEY_PROPERTY',
                title: 'Unconvey Property',
                internalOnly: true,
                specificRoleOnly: true
            }
        ];

        this.authService.getSubjectLoginSuccess().subscribe((flag: boolean) => {
            if (flag === true) {
                setTimeout(() => this.initialize(), 100); // after login
            }
        });

        this.helperService.subjectLaunchPreferences.subscribe((flag: boolean) => {
            if (flag) {
                this.preferenceComponent.show();
            }
        });

        this.helperService.subjectLaunchPortalAnnouncements.subscribe((flag: boolean) => {
            this.displayPortalAnnouncements = flag;
        });

        this.helperService.subjectLaunchResidentAnnouncements.subscribe((flag: boolean) => {
            this.displayResidentAnnouncements = flag;
        });

        this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                const currentRoute = event.url;
                if (!currentRoute.includes('home') && this.isCiraBooksRoute(currentRoute)) {
                    this.isCiraBooksView = true;
                } else {
                    this.isCiraBooksView = false;
                }
            }
        });

        this.hasClassicNavigator = this.navigationService.isTopMenuVisible();
        this.navigationService.getTopMenuStatus().subscribe((flag) => {
            setTimeout(() => (this.hasClassicNavigator = flag), 100);
        });

        this.updateScreenSize();
        this.screenService.changed.subscribe(() => this.updateScreenSize());

        this.updateSelectedContext();
        this.contextSubscription = this.contextManagerService.getContext().subscribe(() => {
            this.updateSelectedContext();
        });

        setTimeout(() => this.initialize(), 100); // first load
    }

    ngAfterViewInit(): void {
        this.updateSelectedContext();
    }

    ngOnDestroy(): void {
        this.contextSubscription.unsubscribe();
    }

    getStatus(rowData: PropertyCommunityInfoResultDto): string {
        if (Utils.notNullAndDefined(rowData)) {
            rowData.communityStatus = this.selectedContext.communityStatus;
            const terminatingDate = this.helperService.getDateString(this.selectedContext.terminatingDate);
            rowData.isTerminating = this.selectedContext.isTerminating;

            return Utils.isTrue(rowData.isTerminating) &&
                Utils.notEqualsIgnoreCase(rowData.communityStatus, 'Terminated')
                ? `Community will terminate on ${terminatingDate}`
                : rowData.communityStatus;
        }
    }

    displayPopover(currentTarget: string, currentPosition: string) {
        this.currentTarget = currentTarget;
        this.currentPosition = currentPosition;
        this.showPopover = true;
    }

    hidePopover() {
        this.showPopover = false;
        this.currentTarget = null;
        this.currentPosition = null;
    }

    getTooltip(): string {
        return Utils.isTrue(this.isNotCurrentOwner) &&
            Utils.notEqualsIgnoreCase(this.selectedContext.communityStatus, 'Terminated')
            ? `${this.selectedContext.customerId}<br><span class="cc-text-required">(Non Current)</span>`
            : this.selectedContext.customerId;
    }

    toggleMenu = () => {
        this.menuOpened = !this.menuOpened;
        localStorage.setItem(this.menuOpenedKey, String(this.menuOpened));
        this.menuToggle.emit();
    };

    showPortalAnnouncements(): void {
        this.displayPortalAnnouncements = true;
    }

    onClosePortalAnnouncement(): void {
        this.displayPortalAnnouncements = false;
    }

    onCloseResidentAnnouncement(): void {
        this.displayResidentAnnouncements = false;
    }

    onReadPortalAnnouncement(): void {
        this.getAnnouncementCount();
    }

    showContextAlerts(): void {
        this.contextAlerts.show(true);
    }

    showPropertyOwnerDetail(): void {
        this.helperService.managePhoneAndEmail();
    }

    showLogAPhoneCall(): void {
        this.helperService.logAPhoneCall();
    }

    onActionClick(item: IActionItem): void {
        if (!!item.path) {
            this.navigateToPath(item.path);
        } else {
            switch (item.code) {
                case 'PROPERTY_CONTACTS':
                    this.showPropertyOwnerDetail();
                    break;
                case 'PROPERTY_OWNER_WELCOME_LETTER':
                    this.showWelcomeLetter();
                    break;
                case 'PROPERTY_LOG_RETURNED_MAIL':
                    this.showLogReturnedMail();
                    break;
                case 'UNCONVEY_PROPERTY':
                    this.getUnconveyanceOwnerInfoEditor();
                    break;
                case 'PROPERTY_CONVEYANCE':
                    this.showConveyPropertyWizard();
                    break;
            }
        }
        this.actionsDropDownButton.instance.close();
    }

    showWelcomeLetter(): void {
        this.displayWelcomeLetter = true;
    }

    onWelcomeLetterClose(e): void {
        this.displayWelcomeLetter = false;
    }

    showLogReturnedMail(): void {
        this.displayLogReturnedMail = true;
    }

    unconveyProperty(): void {
        if (!!this.unconveyanceOwnerInfo) {
            const message = `This action will restore the current owner status from ${this.unconveyanceOwnerInfo.currentOwnerName} (${this.unconveyanceOwnerInfo.currentOwnerId}) to ${this.unconveyanceOwnerInfo.priorOwnerName} (${this.unconveyanceOwnerInfo.priorOwnerId}).<br/>The property currently belongs to community: ${this.unconveyanceOwnerInfo.communityNamesCsv}.<br/>Please note accounting adjustments should be made to both customer accounts.<br/>Select OK to continue or Cancel to make no changes.`;
            const dlgConfirm = this.helperService.getConfirmationDialog(
                'Confirmation',
                message,
                'Ok',
                'Cancel',
                'success',
                'default'
            );
            dlgConfirm.show().then((dialogResult) => {
                if (dialogResult === true) {
                    const params = new ConveyancePropertyRawDto();
                    params.communityId = this.selectedContext.communityId;
                    params.customerId = this.selectedContext.customerId;
                    this.loadingVisible = true;
                    this.propertyConveyanceService.unconveyProperty(params).subscribe(
                        (response: UnconveyPropertyResultDto) => {
                            this.loadingVisible = false;
                            let successMessage = `Ownership Status has been successfully changed from from ${this.unconveyanceOwnerInfo.currentOwnerName} (${this.unconveyanceOwnerInfo.currentOwnerId}) to ${this.unconveyanceOwnerInfo.priorOwnerName} (${this.unconveyanceOwnerInfo.priorOwnerId}).`;
                            const noOfQuarantinedPayments = response.numberOfQuarantinedPayments;
                            if (noOfQuarantinedPayments > 0) {
                                successMessage +=
                                    `<br/>Owner ${this.unconveyanceOwnerInfo.currentOwnerName} currently has ` +
                                    (noOfQuarantinedPayments > 1
                                        ? `${noOfQuarantinedPayments} quarantined payments.`
                                        : `${noOfQuarantinedPayments} quarantined payment.`);
                            }
                            this.unconveyanceMessageBox.showSuccessMessage('Success', successMessage, () => {
                                this.router.navigate(['/home']);
                            });
                        },
                        (error) => {
                            this.loadingVisible = false;
                            this.errorService.displayError(error);
                        }
                    );
                }
            });
        }
    }

    showConveyPropertyWizard(): void {
        this.conveyPropertyUrl = `my-work/property-conveyance/${this.selectedContext.communityId}/${this.selectedContext.customerId}/convey-property`;
        setTimeout(() => {
            this.showConveyProperty = true;
        }, 500);
    }

    onCloseLogReturnedMail(): void {
        this.displayLogReturnedMail = false;
    }

    navigateToPath(path: string): void {
        const urlPath = this.helperService.getUrlForCurrentContext(path);
        if (this.helperService.isOpenLinksExternal()) {
            this.helperService.openWindow(urlPath, '_blank', null, null);
        } else {
            this.router.navigate([urlPath]);
        }
    }

    onPreferenceClose(flag: boolean): void {
        this.helperService.subjectLaunchPreferences.next(false);
    }

    onCommunityClick(rec: KeyValueResultDto): void {
        if (rec.key !== this.selectedContext.communityId) {
            const url = window.location.href.replace(`/${this.selectedContext.communityId}/`, '/:communityId/');
            window.open(url.replace('/:communityId/', `/${rec.key}/`), '_blank').focus();
        }
        this.showCommunities = false;
    }

    onPropertyConveyanceShown(): void {
        setTimeout(() => {
            const frameElement = document.getElementById('conveyFrameElement');
            if (Utils.notNullAndDefined(frameElement)) {
                (frameElement as any).onEditorFinish = this.onPropertyConveyanceHide.bind(this);
            }
        }, 5000);
    }

    onPropertyConveyanceHide(): void {
        this.conveyPropertyUrl = null;
        this.showConveyProperty = false;
    }

    onSessionEndsClick(): void {
        const validityTime = this.authService.getTokenValidityTime();
        if (validityTime > 0) {
            const expiresInTime = 2 * 60 * 60 * 1000; // 2 hrs
            this.showExtendSession = expiresInTime > validityTime;
        } else {
            alert(`Your session has expired. You will be logged out`, `Session Ended`).then(() => {
                this.authService.logOut();
                this.contextManagerService.updateContext(null); // reset context once logout
            });
        }
    }

    doExtendSession(submit: boolean): void {
        if (Utils.isTrue(submit) && Utils.notNullOrEmpty(this.userPassword)) {
            this.loadingVisible = true;
            this.authService.extendSession(this.userPassword).subscribe(
                (success: boolean) => {
                    if (Utils.isTrue(success)) {
                        notify('Session has been renewed successfully', 'success', MessageInterval.successMsgInterval);
                        this.showExtendSession = false;
                        this.updateSessionTime();
                    } else {
                        notify(
                            'Authentication failed. Please verify your password',
                            'error',
                            MessageInterval.errorMsgInterval
                        );
                    }
                    this.loadingVisible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        } else {
            this.showExtendSession = submit;
        }
    }

    onShowBoardApprovalClick(): void {
        /**
         * If count is zero navigate to portfolio board approval
         * Else if count is greter then zero, navigate to community context board approval
         */

        if (this.baordApprovalOpenItemsCount > 0 && Utils.notNullAndDefined(this.selectedContext.communityId)) {
            const route = `community/${this.selectedContext.communityId}/my-work/board-approval`;
            this.router.navigate([route]);
        } else if (Utils.equals(this.baordApprovalOpenItemsCount, 0)) {
            /**
             * TODO: Portfolio Board Approval is in QA, hence commented the code.
             * Once Portfolio Board Approval goes to production, uncomment the router navigation
             */
            // Navigate to portfolio board approval
            // this.router.navigate(['my-work/board-approval']);
        }
    }

    private initialize(): void {
        this.isInternalUser = this.userService.isInternal();

        this.autoShowAnnouncements = true;
        this.userMenuItems = [];

        this.updateSessionTime();

        if (this.userService.isITUser()) {
            this.userMenuItems.push({
                text: 'Manage QA Users',
                icon: 'fas fa-vial',
                onClick: () => {
                    this.showManageQaUsers = true;
                }
            });
        }

        this.userMenuItems.push({
            text: 'Preferences',
            icon: 'preferences',
            onClick: () => {
                this.preferenceComponent.show();
            }
        });

        this.userMenuItems.push({
            text: 'Logout',
            icon: 'fas fa-sign-out-alt',
            onClick: () => {
                this.authService.logOut();
                this.contextManagerService.updateContext(null); // reset context once logout
            }
        });

        if (this.authService.isLoggedIn) {
            const menuOpened = localStorage.getItem(this.menuOpenedKey) || 'true';
            if (menuOpened === 'false') {
                setTimeout(this.toggleMenu.bind(this), 1500);
            }
        }

        this.autoShowAnnouncements =
            (this.localStorageService.getItem(
                StorageKeyConstants.userShowAnnouncementsKey,
                CCLocalStorageCategory.PREF
            ) || 'true') === 'true';

        this.getOpenItemCount();
    }

    private updateSessionTime(): void {
        const minuteTick = 60000;
        const validityTime = this.authService.getTokenValidityTime();
        if (validityTime > 0) {
            const validityHrs = Math.min(Math.floor(validityTime / (60 * minuteTick)), 24);
            const validityMins = Math.min(Math.ceil((validityTime - validityHrs * 60 * minuteTick) / minuteTick), 60);
            this.sessionEndsIn = [
                `${validityHrs.toString().padStart(2, '0')}`,
                `${validityMins.toString().padStart(2, '0')}`
            ];
            // Call self after every minute
            setTimeout(() => this.updateSessionTime(), minuteTick);
        } else {
            this.sessionEndsIn = [`00`, `00`];
        }
    }

    private getUnconveyanceOwnerInfoEditor() {
        this.loadingVisible = true;
        const params = new UnconveyPropertyParametersDto();
        params.communityId = this.selectedContext.communityId;
        params.customerId = this.selectedContext.customerId;
        this.unconveyanceOwnerInfo = null;
        this.propertyConveyanceService.getUnconveyPropertyEditor(params).subscribe(
            (response: UnconveyancePropertyEditorResultDto) => {
                this.loadingVisible = false;
                if (!!response && !!response.currentOwnerName && !!response.priorOwnerName) {
                    this.unconveyanceOwnerInfo = response;
                    this.unconveyProperty();
                } else {
                    this.helperService.getOkDialog('Info', 'This property can not be unconveyed.').show();
                }
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
            }
        );
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        const screenSize = this.screenService.getCurrentScreenSize;
        const screenParams = this.screenService.getScreenParams(screenSize).header;
        this.logoLocation = screenParams.logoLocation;
        this.menuButtonWidth = screenParams.menuButtonWidth;
    }

    private updateSelectedContext(): void {
        this.selectedContext = this.contextManagerService.getSelectedContext();

        this.isCommunityInProduction =
            Utils.notNullAndDefined(this.selectedContext) &&
            Utils.equalsIgnoreCase(this.selectedContext.communityStatus, 'InProduction');
        this.isNotCurrentOwner =
            Utils.notNullAndDefined(this.selectedContext) && Utils.isFalse(this.selectedContext.isCurrentOwner);
        this.hasContext =
            !!this.selectedContext && (!!this.selectedContext.communityId || !!this.selectedContext.customerId);

        this.showOwnerName =
            !this.isSmallScreen ||
            (!!this.selectedContext &&
                `${this.selectedContext.propertyAddress} (${this.selectedContext.ownerName})`.length < 100);

        this.hasMultipleCommunities =
            !!this.selectedContext &&
            !!this.selectedContext.customerId &&
            this.selectedContext.propertyCommunities.length > 1;

        if (this.hasMultipleCommunities) {
            this.otherCommunities = this.selectedContext.propertyCommunities.filter(
                (r) => r.key !== this.selectedContext.communityId
            );
        }

        if (this.authService.isLoggedIn) {
            this.getActionList();
            this.getAnnouncementCount();
            this.getBoardApprovalOpenItemsCount();
        }
    }

    private getActionList(): void {
        if (this.userService.isContextReady(this.selectedContext)) {
            const params = new ActionAccessListParameterDto();

            if (Utils.notNullAndDefined(this.selectedContext)) {
                params.communityId = this.selectedContext.communityId || params.communityId;
                params.customerId = this.selectedContext.customerId || params.customerId;
            }

            this.userService.getActionList(params).subscribe((actions: ActionAccessResultDto[]) => {
                // filter out unconvey code.
                const unconveyAction = actions.filter((item) => Utils.equalsIgnoreCase(item.code, 'UNCONVEY_PROPERTY'));
                if (Utils.isNullOrEmpty(unconveyAction)) {
                    this.actionItems = this.actionItems.filter(
                        (item) => !Utils.equalsIgnoreCase(item.code, 'UNCONVEY_PROPERTY')
                    );
                }

                this.actionItems.forEach((action) => {
                    actions.forEach((e) => {
                        if (Utils.equalsIgnoreCase(action.code, e.code)) {
                            action.internalOnly = e.internalOnly;
                            action.specificRoleOnly = e.specificRoleOnly;
                        }
                    });
                });

                if (
                    Utils.notNullAndDefined(this.selectedContext) &&
                    Utils.notNullAndDefined(this.selectedContext.communityId)
                ) {
                    /**
                     * When we are in community, Find board approval action code for community context
                     */

                    const communityBoardApprovalActionCode = actions.find((item) =>
                        Utils.equalsIgnoreCase(item.code, 'BOARD_APPROVALS')
                    );

                    // Show Board approval bell icon
                    this.hasBoardApprovalAccess = Utils.notNullAndDefined(communityBoardApprovalActionCode);
                }
            });
        } else {
            /**
             * TODO: Showing board approval notification icon only in community context now as Board approval at portfolio context is in QA
             * Once Board Approval at Portfolio context goes to production we need to update the logic for action code 'PORTFOLIO_BOARD_APPROVALS'
             */

            this.hasBoardApprovalAccess = false;
        }
    }

    private getAnnouncementCount() {
        this.portalAnnoucementService.getAnnouncementCount().subscribe(
            (announcementCount) => {
                this.announcementCount = announcementCount;
                const previousCount = parseInt(
                    this.localStorageService.getItem(
                        StorageKeyConstants.userAnnouncementsCountKey,
                        CCLocalStorageCategory.PREF
                    ) || '0'
                );
                if (this.autoShowAnnouncements && announcementCount > previousCount) {
                    this.displayPortalAnnouncements = announcementCount > 0;
                }
                this.localStorageService.setItem(
                    StorageKeyConstants.userAnnouncementsCountKey,
                    announcementCount.toFixed(0),
                    CCLocalStorageCategory.PREF
                );
            },
            (error) => {
                this.errorService.displayError(error);
            }
        );
    }

    private getBoardApprovalOpenItemsCount(): void {
        // Get the open items count for board approval at community context
        if (
            Utils.notNullAndDefined(this.selectedContext) &&
            Utils.notNullAndDefined(this.selectedContext.communityId)
        ) {
            // Make API call to get open items count
            this.dashboardService.getBoardApporvalOpenItemsCount(this.selectedContext.communityId).subscribe(
                (response: BoardApprovalOpenItemCountResultDto) => {
                    this.baordApprovalOpenItemsCount = response.count;
                },
                () => {
                    // Not showing any error. keep it silent and set the count to zero
                    this.baordApprovalOpenItemsCount = 0;
                }
            );
        } else if (Utils.isNullOrUndefined(this.selectedContext)) {
            // Set the count to zero
            this.baordApprovalOpenItemsCount = 0;
        }
    }

    private get menuOpenedKey(): string {
        const currentUserId = this.userService.getCurrentUserId();
        if (Utils.notNullOrEmpty(currentUserId)) {
            return `MP_${currentUserId.toUpperCase()}_PREF_CC_USER_SIDEMENU_OPENED`;
        } else {
            const customDialog = custom({
                title: 'Session Ended',
                messageHtml: `Your session has expired. Please log in again to continue.`,
                buttons: [
                    {
                        text: 'Ok',
                        hint: 'Ok',
                        type: 'default',
                        onClick: () => {
                            this.authService.logOut();
                            this.contextManagerService.updateContext(null); // reset context once logout
                        }
                    }
                ]
            });
            customDialog.show();
            return '';
        }
    }

    private isCiraBooksRoute(currentRoute: string): boolean {
        return (
            currentRoute.includes('/cb/') ||
            currentRoute.includes('/vendormissingbills') ||
            currentRoute.includes('/searchinvoices') ||
            currentRoute.includes('/reimbursementrequest') ||
            currentRoute.includes('/managevendoredit')
        );
    }

    private getOpenItemCount(): void {
        // TODO API Call to get Open Item Count
    }
}
